import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { Subject, bufferTime, filter } from 'rxjs';

import { AlertaService } from '@shared/services/alerta.service';
import { inject } from '@angular/core';
import { environment } from '@env/environment';
import { AuthService } from '@auth/services/auth.service';

// 1. Subject para capturar los errores procesados
const errorStream$ = new Subject<{ message: string; status: number; detail: string }>();

let isSubscriberInitialized = false;

function initErrorBuffer(alertaService: AlertaService) {
  if (isSubscriberInitialized) return;
  isSubscriberInitialized = true;

  errorStream$
    .pipe(
      bufferTime(1000), // Agrupa errores en una ventana de 400ms
      filter((errores) => errores.length > 0),
    )
    .forEach((errores) => {
      // Si solo hay un error, mantenemos el comportamiento individual
      if (errores.length === 1) {
        const err = errores[0];
        if (err.status === 412) {
          alertaService.info(err.message, { esHtml: true });
        } else {
          alertaService.error(
            `${err.message} <br><small>${!environment.production ? err.detail : ''}</small>`,
            { esHtml: true },
          );
        }
        return;
      }

      // Si hay MÚLTIPLES errores, construimos una lista detallada
      let listaHtml = `<strong>Se han producido varios errores (${errores.length}):</strong><br>`;
      listaHtml += `<ul style="text-align: left; margin: 8px 0 0 15px; padding-left: 0; max-height: 150px; overflow-y: auto;">`;

      errores.forEach((err) => {
        listaHtml += `<li style="margin-bottom: 4px;">${err.message}`;
        if (err.detail && err.detail !== err.message && !environment.production) {
          listaHtml += ` <br><small style="color: inherit; opacity: 0.8;">(${err.detail})</small>`;
        }
        listaHtml += `</li>`;
      });

      listaHtml += `</ul>`;

      // Mostramos la alerta única con la lista completa
      alertaService.error(listaHtml, { esHtml: true });
    });
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const alertaService = inject(AlertaService);
  const authService = inject(AuthService);

  initErrorBuffer(alertaService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.error instanceof Blob && error.error.type === 'application/json') {
        return from(error.error.text()).pipe(
          switchMap((errorText) => {
            const parsedError = JSON.parse(errorText);
            const reconstructedError = new HttpErrorResponse({
              error: parsedError,
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url || undefined,
            });

            procesarErrorYAgrupar(reconstructedError, authService);
            return throwError(() => reconstructedError);
          }),
        );
      }

      procesarErrorYAgrupar(error, authService);
      return throwError(() => error);
    }),
  );
};

function procesarErrorYAgrupar(error: HttpErrorResponse, authService: AuthService) {
  if (!environment.production) {
    console.log('Error interceptado:', error);
  }

  let errorMessage = 'Ha ocurrido un error inesperado.';
  let errorDetail = error.message;

  if (error.error instanceof ErrorEvent) {
    errorMessage = `Error de conexión: ${error.error.message}`;
  } else {
    switch (error.status) {
      case 401:
        authService.logout();
        return; // El 401 corta y redirige de inmediato
      case 404:
        errorMessage = '🔗 Recurso no encontrado (404).';
        break;
      case 412:
        errorMessage = `${error.error?.message || 'Precondición fallida (412)'}`;
        break;
      case 500:
        errorMessage = '🚨 Error interno del servidor (500).';
        break;
      default:
        errorMessage = `❌ Error HTTP ${error.status}: ${error.error?.message || ''}`;
    }
    errorDetail = error.message;
  }

  console.error(`Error interceptado: ${errorMessage}`, errorDetail);

  // Enviamos cada error individual al flujo para que el buffer los junte
  errorStream$.next({
    message: errorMessage,
    status: error.status,
    detail: errorDetail,
  });
}
